import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import CircularProgress from '@mui/material/CircularProgress';
import SendIcon from '@mui/icons-material/Send';
import { Plus, Trash2, MessageSquare, Pencil, Check, X, Square } from 'lucide-react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip as RTooltip, ResponsiveContainer,
} from 'recharts';
import {
    createConversation,
    listConversations,
    getConversation,
    appendTurn,
    updateConversation,
    deleteConversation,
    messageToTurnContent,
    turnToMessage,
} from '../services/chatHistory';
import '../styles/CompanyChat.css';

// Mint a UUID. Same id is sent to Node (chat_conversations.id) and Flask
// (conversation_id) so both sides agree on the thread identity.
const mintConversationId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    // Fallback for very old browsers; should never hit in modern Chrome/Safari.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
    });
};

// Truncate a string to N chars with ellipsis.
const truncate = (s, n = 60) => {
    if (!s) return '';
    return s.length > n ? `${s.slice(0, n - 1)}…` : s;
};

// Pick a display title for a conversation row in the sidebar.
const conversationDisplayTitle = (conv) => {
    if (conv?.title) return conv.title;
    const preview = conv?.last_turn_preview;
    if (typeof preview === 'string' && preview.length > 0) return truncate(preview);
    return 'New chat';
};

// ─── Module-level concurrent-chat store ────────────────────────────────
// Lives outside React so /agent/query results land in the right conversation
// even after the user has switched views or kicked off another chat.
const _messagesByConv = new Map();   // convId → Message[]
const _runningByConv = new Set();    // convId → currently in-flight
const _unreadByConv = new Set();     // convId → has a result the user hasn't seen
const _abortByConv = new Map();      // convId → AbortController for the live request
const _storeListeners = new Set();
const _notifyStore = () => _storeListeners.forEach(fn => fn());

const setMessagesForConv = (convId, msgs) => {
    if (!convId) return;
    if (typeof msgs === 'function') {
        msgs = msgs(_messagesByConv.get(convId) || []);
    }
    _messagesByConv.set(convId, msgs);
    _notifyStore();
};
const getMessagesForConv = (convId) => (convId ? (_messagesByConv.get(convId) || []) : []);
const startRunning = (convId) => { if (convId) { _runningByConv.add(convId); _notifyStore(); } };
const stopRunning = (convId) => { if (convId && _runningByConv.delete(convId)) _notifyStore(); };
const isRunning = (convId) => !!convId && _runningByConv.has(convId);
const markUnread = (convId) => { if (convId) { _unreadByConv.add(convId); _notifyStore(); } };
const markRead = (convId) => { if (convId && _unreadByConv.delete(convId)) _notifyStore(); };
const isUnread = (convId) => !!convId && _unreadByConv.has(convId);

// Abort the in-flight request for a conv. The running flag is cleared by
// the request's finally block; this just signals fetch to bail.
const abortConv = (convId) => {
    if (!convId) return;
    const ctrl = _abortByConv.get(convId);
    if (ctrl) {
        try { ctrl.abort(); } catch (_) { /* ignore */ }
    }
};

// Format updated_at as a short relative-ish label for the sidebar.
const formatUpdatedAt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    const oneDay = 24 * 60 * 60 * 1000;
    const diffDays = Math.floor((now - d) / oneDay);
    if (diffDays < 7) return d.toLocaleDateString(undefined, { weekday: 'short' });
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const AUTO_EXECUTE_THRESHOLD = 0.85;

// Render `**bold**` segments as <strong>; everything else as plain text.
const renderBoldMarkdown = (str) => {
    if (str == null) return null;
    const parts = String(str).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
        const m = p.match(/^\*\*([^*]+)\*\*$/);
        return m
            ? <strong key={i}>{m[1]}</strong>
            : <React.Fragment key={i}>{p}</React.Fragment>;
    });
};

// Patterns we use to classify timeseries field names.
const TIME_KEY_PATTERNS = ['bucket_start', 'timestamp', 'date', 'period', 'month', 'week', 'day', 'time'];
const VOLUME_KEY_PATTERNS = ['post', 'count', 'volume', 'n_post', 'sample_size', 'total'];
const METRIC_KEY_PATTERNS = ['nps', 'score', 'rating', 'sentiment', 'mean', 'avg', 'percent', 'ratio', 'pct', 'value'];

const matchesAny = (key, patterns) => {
    const lk = String(key).toLowerCase();
    return patterns.some(p => lk.includes(p));
};
const isDateLikeString = (v) => typeof v === 'string' && /^\d{4}-\d{2}(-\d{2})?/.test(v);

// Convert ISO week (YYYY-Www) to the Monday of that week.
// Uses the standard "ISO 8601 — week 1 is the week containing the first Thursday" rule.
const isoWeekToDate = (year, week) => {
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const dow = jan4.getUTCDay() || 7;  // Sun=7, Mon=1
    const monday = new Date(jan4);
    monday.setUTCDate(jan4.getUTCDate() - (dow - 1) + (week - 1) * 7);
    return monday;
};

// Format X-axis ticks: ISO weeks / ISO dates / unix-ms → short labels.
const formatTick = (val) => {
    if (val == null) return '';
    if (typeof val === 'string') {
        // ISO week: "2025-W49"
        const wm = val.match(/^(\d{4})-W(\d{1,2})$/i);
        if (wm) {
            const d = isoWeekToDate(parseInt(wm[1], 10), parseInt(wm[2], 10));
            if (!isNaN(d.getTime())) {
                return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
            }
        }
        // ISO month: "2025-12"
        const mm = val.match(/^(\d{4})-(\d{2})$/);
        if (mm) {
            const d = new Date(Date.UTC(parseInt(mm[1], 10), parseInt(mm[2], 10) - 1, 1));
            if (!isNaN(d.getTime())) {
                return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
            }
        }
        if (isDateLikeString(val)) {
            const d = new Date(val);
            if (!isNaN(d.getTime())) {
                return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
            }
        }
    }
    if (typeof val === 'number' && val > 1e10) {
        // Looks like a unix-ms timestamp
        const d = new Date(val);
        if (!isNaN(d.getTime())) {
            return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        }
    }
    return val;
};

// Pull a usable {data, xKey, yKey, label} from a flexible timeseries shape.
// Accepts `{points: [...]} | array`. Distinguishes time fields from numeric metric
// fields, and de-prioritises post-volume fields when picking Y.
const normaliseTimeseries = (ts) => {
    if (!ts) return null;
    const arr = Array.isArray(ts) ? ts : (Array.isArray(ts.points) ? ts.points : null);
    if (!arr || arr.length === 0) return null;
    const sample = arr[0] || {};
    const keys = Object.keys(sample);
    if (keys.length === 0) return null;

    // X: explicit > name-matched time field > value-shaped-like-date.
    // We DO NOT fall back to "first key" because that's what produced the
    // both-axes-are-post-volume bug.
    let xKey = ts.x_key && keys.includes(ts.x_key) ? ts.x_key : null;
    if (!xKey) xKey = keys.find(k => matchesAny(k, TIME_KEY_PATTERNS));
    if (!xKey) xKey = keys.find(k => isDateLikeString(sample[k]));
    if (!xKey) {
        console.warn('[CompanyChat] timeseries: no time-shaped field found in sample:', sample);
        return null;
    }

    // Y: explicit > metric-named numeric > non-volume numeric > any numeric > volume numeric.
    let yKey = ts.y_key && keys.includes(ts.y_key) ? ts.y_key : null;
    if (!yKey) {
        const numericKeys = keys.filter(k => k !== xKey && typeof sample[k] === 'number');
        yKey = numericKeys.find(k => matchesAny(k, METRIC_KEY_PATTERNS))
            || numericKeys.find(k => !matchesAny(k, VOLUME_KEY_PATTERNS))
            || numericKeys[0];
    }
    if (!yKey) {
        console.warn('[CompanyChat] timeseries: no numeric Y field found in sample:', sample);
        return null;
    }

    return {
        data: arr,
        xKey,
        yKey,
        label: ts.label || ts.metric || ts.metric_name || yKey,
    };
};

// Predefined options for fields the agent commonly needs disambiguated.
// Each option carries `patch` — a partial update merged into the plan field on confirm.
// Plain client-side overrides for chip labels — keeps the UI clean of backend
// implementation detail. Keyed by `${field}:${value}`. Falls back to opt.label.
const CHIP_LABEL_OVERRIDES = {
    'mode_intent:fast': 'Fast mode, 30-60 seconds',
    'mode_intent:deep': 'Deep thinking, 2-5 minutes',
};

// Group clarifier_options[] by their `field` key. Preserves array order within each group.
const groupClarifierOptions = (options) => {
    const groups = {};
    if (!Array.isArray(options)) return groups;
    for (const opt of options) {
        if (!opt || typeof opt !== 'object' || !opt.field) continue;
        if (!groups[opt.field]) groups[opt.field] = [];
        groups[opt.field].push(opt);
    }
    return groups;
};

// Within a group, the option flagged `suggested: true` is the default selection.
const defaultIndexFor = (groupOpts) => {
    if (!Array.isArray(groupOpts) || groupOpts.length === 0) return null;
    const i = groupOpts.findIndex(o => o?.suggested === true);
    return i >= 0 ? i : null;
};

const TIME_RANGE_OPTIONS = [
    { label: 'All time',       patch: { label: 'all time',       days_back: null,  start: null, end: null } },
    { label: 'Last 30 days',   patch: { label: 'last 30 days',   days_back: 30 } },
    { label: 'Last 90 days',   patch: { label: 'last 90 days',   days_back: 90 } },
    { label: 'Last 6 months',  patch: { label: 'last 6 months',  days_back: 180 } },
    { label: 'Last year',      patch: { label: 'last year',      days_back: 365 } },
    { label: 'Last 2 years',   patch: { label: 'last 2 years',   days_back: 730 } },
    { label: 'YTD',            patch: { label: 'year to date',   days_back: null, ytd: true } },
];

const FIELD_LABELS = {
    time_range: 'Time range',
    topic: 'Topic',
    query_type: 'Query type',
    intent: 'Intent',
    output_style: 'Output style',
};

// A plan field needs user input if the backend asked for it (hint set) or marked it missing.
const detectMissingFields = (plan) => {
    if (!plan || typeof plan !== 'object') return [];
    const out = [];
    for (const [key, value] of Object.entries(plan)) {
        if (!value || typeof value !== 'object') continue;
        if (value.missing === true || (typeof value.hint === 'string' && value.hint.length > 0)) {
            out.push({ key, value });
        }
    }
    return out;
};

// Build a "Confirm: <inferred>" chip when the field already carries an inferred value.
// Empty patch = pass the field through unchanged; outer logic still flips confidence/missing/hint.
const confirmChipFor = (key, fieldValue) => {
    if (!fieldValue || typeof fieldValue !== 'object') return null;
    if (key === 'time_range') {
        const hasValue =
            fieldValue.label != null ||
            fieldValue.days_back != null ||
            fieldValue.start != null ||
            fieldValue.end != null;
        if (!hasValue) return null;
        const display = fieldValue.label
            || (fieldValue.days_back != null ? `last ${fieldValue.days_back} days` : null)
            || (fieldValue.start && fieldValue.end ? `${fieldValue.start} – ${fieldValue.end}` : 'assumption');
        return { label: `Confirm: ${display}`, patch: {} };
    }
    if (key === 'topic') {
        if (fieldValue.topic_id) {
            return {
                label: `Confirm: ${fieldValue.topic_name || 'this topic'}`,
                patch: {
                    topic_id: fieldValue.topic_id,
                    topic_name: fieldValue.topic_name,
                    ambiguous: false,
                    missing: false,
                },
            };
        }
    }
    return null;
};

// Build the radio options for a given field. Returns [] if we can't suggest any.
const optionsForField = (key, fieldValue) => {
    const opts = [];
    const confirmOpt = confirmChipFor(key, fieldValue);
    if (confirmOpt) opts.push(confirmOpt);

    if (key === 'time_range') {
        opts.push(...TIME_RANGE_OPTIONS);
    } else if (key === 'topic' && Array.isArray(fieldValue?.alternatives) && fieldValue.alternatives.length > 0) {
        fieldValue.alternatives.forEach(alt => {
            opts.push({
                label: alt.topic_name || alt.name || alt.label || 'Option',
                patch: {
                    topic_id: alt.topic_id || alt.id,
                    topic_name: alt.topic_name || alt.name,
                    ambiguous: false,
                    missing: false,
                },
            });
        });
    }
    return opts;
};

const CompanyChat = ({ companyId }) => {
    const [input, setInput] = useState('');
    const [conversationId, setConversationId] = useState(null);

    // Subscribe to module-level store so the component re-renders when any
    // running chat updates — even ones the user isn't currently viewing.
    const [, _forceStoreSync] = useState(0);
    useEffect(() => {
        const fn = () => _forceStoreSync(n => n + 1);
        _storeListeners.add(fn);
        return () => { _storeListeners.delete(fn); };
    }, []);

    // Active conversation's messages and loading state — derived from the store.
    const messages = getMessagesForConv(conversationId);
    const isLoading = isRunning(conversationId);

    // Ref tracking the active conv id, for async-time comparisons (e.g.,
    // deciding whether to mark a result as unread).
    const conversationIdRef = useRef(conversationId);
    useEffect(() => { conversationIdRef.current = conversationId; }, [conversationId]);


    // { [messageIdx]: { [fieldKey]: optionIndex } }
    const [clarifierSelections, setClarifierSelections] = useState({});
    // { [messageIdx]: typedTopicName } — used when Flask asks for topic but
    // doesn't supply clarifier_options[] with topic alternatives.
    const [clarifierTopicText, setClarifierTopicText] = useState({});

    // Sidebar conversation list (lightweight rows, no full turns).
    const [conversations, setConversations] = useState([]);
    const [conversationsLoading, setConversationsLoading] = useState(false);

    // Browser notification — fired when a backgrounded chat finishes while
    // the tab isn't focused. Permission is requested lazily on first use.
    const maybeFireBrowserNotification = useCallback((convId, agentMsg) => {
        if (typeof Notification === 'undefined') return;
        if (!document.hidden) return;  // user can see the page; skip
        const trigger = () => {
            try {
                const conv = conversations.find(c => c.id === convId);
                const title = conv?.title || 'Research Assistant';
                const body = agentMsg?.headline
                    || (agentMsg?.type === 'error' ? 'Chat finished with an error.' : 'Chat finished — open to view results.');
                new Notification(title, { body });
            } catch (_) { /* ignore */ }
        };
        if (Notification.permission === 'granted') {
            trigger();
        } else if (Notification.permission !== 'denied') {
            Notification.requestPermission().then(p => { if (p === 'granted') trigger(); });
        }
    }, [conversations]);

    // Edit-fork state — index of the user message being edited; null when not editing.
    const [editingIdx, setEditingIdx] = useState(null);
    const [editingDraft, setEditingDraft] = useState('');

    const { flaskServerAddress, userId, userDetails } = useAuth();
    const orgId = userDetails?.user_org?.id;

    // Load the conversation list on mount and after creates/deletes.
    const refreshConversations = useCallback(async () => {
        setConversationsLoading(true);
        try {
            const list = await listConversations();
            setConversations(Array.isArray(list) ? list : []);
        } catch (err) {
            // Endpoints may not be live yet — degrade silently.
            console.warn('[CompanyChat] listConversations unavailable:', err?.message);
            setConversations([]);
        } finally {
            setConversationsLoading(false);
        }
    }, []);

    useEffect(() => { refreshConversations(); }, [refreshConversations]);

    // Index of the last non-deleted user message, or -1.
    const lastUserMessageIdx = useMemo(() => {
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i]?.type === 'user') return i;
        }
        return -1;
    }, [messages]);

    // Best-effort persist a turn. Swallows errors so chat keeps working
    // even if Node endpoints aren't live yet.
    const persistTurn = useCallback(async (convId, message) => {
        if (!convId || !message) return;
        try {
            await appendTurn(convId, {
                role: message.type,
                content: messageToTurnContent(message),
                companyId: companyId || null,
            });
        } catch (err) {
            console.warn('[CompanyChat] appendTurn failed:', err?.message);
        }
    }, [companyId]);

    // Ensure a conversation exists. Returns the id (existing or newly created).
    // Also seeds the local sidebar list with the new row so it shows up
    // immediately, before the next list refresh.
    const ensureConversation = useCallback(async () => {
        if (conversationId) return conversationId;
        const newId = mintConversationId();
        try {
            const row = await createConversation({ id: newId, companyId: companyId || null });
            const created = row || { id: newId, title: null, company_ids: companyId ? [companyId] : [], updated_at: new Date().toISOString() };
            setConversations(prev => [created, ...prev]);
        } catch (err) {
            console.warn('[CompanyChat] createConversation failed (continuing locally):', err?.message);
        }
        setConversationId(newId);
        return newId;
    }, [conversationId, companyId]);

    // POST /agent/query — single network helper used by both initial and follow-up turns
    const callAgent = async (body, { signal } = {}) => {
        const base = flaskServerAddress || '';
        const url = `${base}${base.endsWith('/') ? '' : '/'}agent/query`;
        const token = localStorage.getItem('token');
        const resp = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(body),
            signal,
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
            const err = data?.error || `Request failed (${resp.status})`;
            throw new Error(err);
        }
        return data;
    };

    // Build the React message for an /agent/query response (single source of
    // truth for the shape; used by both UI render and DB persistence).
    const buildAgentMessage = (data, fallbackQuestion) => {
        if (data?.status === 'needs_confirmation') {
            return {
                type: 'clarifier',
                plan: data.plan,
                followUp: data.follow_up,
                originalQuestion: fallbackQuestion,
                conversationId: data.conversation_id || conversationId,
                clarifierOptions: Array.isArray(data.clarifier_options) ? data.clarifier_options : [],
                suggestedMode: data.suggested_mode || data.plan?.suggested_mode || null,
            };
        }
        if (data?.status === 'executed') {
            return {
                type: 'assistant',
                headline: data.headline,
                narrative: Array.isArray(data.narrative) ? data.narrative : [],
                timeseries: data.timeseries,
                sources: data.sources,
                confidence: data.confidence,
                route: data.route,
                metadata: data.metadata,
            };
        }
        if (data?.error) {
            return { type: 'error', content: String(data.error) };
        }
        return { type: 'error', content: 'Unexpected response from agent.' };
    };

    // Append the assistant-side outcome of a /agent/query response and
    // return the message we appended (so callers can persist it).
    // `targetConvId` is captured at request-start time so the result lands
    // in the right conversation even if the user has switched views.
    const appendAgentResponse = (data, fallbackQuestion, targetConvId) => {
        const msg = buildAgentMessage(data, fallbackQuestion);
        if (targetConvId) {
            setMessagesForConv(targetConvId, prev => [...prev, msg]);
        }
        return { msg, agentTitle: data?.title || null };
    };

    const sendMessage = async (message) => {
        const isFirstTurn = !conversationId;
        const userMsg = { type: 'user', content: message };

        // Ensure we have a conversation row before firing off the persist call.
        const convId = await ensureConversation();
        // Capture convId for async writes — user may switch views before this completes.
        setMessagesForConv(convId, prev => [...prev, userMsg]);
        startRunning(convId);

        const abortCtrl = new AbortController();
        _abortByConv.set(convId, abortCtrl);

        // Persist user turn (best-effort).
        persistTurn(convId, userMsg);

        // On the first turn, set a fallback title locally so the sidebar isn't
        // empty if Flask doesn't supply one. Title can be overwritten when
        // /agent/query response includes a generated title.
        if (isFirstTurn) {
            const fallbackTitle = truncate(message);
            setConversations(prev => prev.map(c => (
                c.id === convId && !c.title ? { ...c, title: fallbackTitle, updated_at: new Date().toISOString() } : c
            )));
            updateConversation(convId, { title: fallbackTitle }).catch(err => {
                console.warn('[CompanyChat] title patch failed:', err?.message);
            });
        }

        try {
            // Always include scope fields — Flask may need them on follow-ups
            // (e.g., when conversation_id isn't found on its side and it
            // re-creates state) and on first turns.
            const body = {
                question: message,
                conversation_id: convId,
            };
            if (companyId) body.company_id = companyId;
            if (orgId) body.organization_id = orgId;
            if (userId) body.user_id = userId;
            if (isFirstTurn) {
                body.start_conversation = true;
                body.auto_execute_threshold = AUTO_EXECUTE_THRESHOLD;
            }

            const data = await callAgent(body, { signal: abortCtrl.signal });
            const { msg: agentMsg, agentTitle } = appendAgentResponse(data, message, convId);

            // If Flask returns a generated title on turn 1, prefer it over the fallback.
            if (isFirstTurn && agentTitle) {
                setConversations(prev => prev.map(c => (c.id === convId ? { ...c, title: agentTitle } : c)));
                updateConversation(convId, { title: agentTitle }).catch(() => {});
            }

            persistTurn(convId, agentMsg);
            // Bump local sidebar updated_at so this thread floats to the top.
            setConversations(prev => prev.map(c => (
                c.id === convId ? { ...c, updated_at: new Date().toISOString() } : c
            )));

            // If the user has switched away from this conv, flag unread + maybe notify.
            if (convId !== conversationIdRef.current) {
                markUnread(convId);
                maybeFireBrowserNotification(convId, agentMsg);
            }
        } catch (err) {
            const isAbort = err?.name === 'AbortError';
            const stoppedMsg = { type: 'error', content: isAbort ? 'Stopped.' : (err?.message || 'Sorry, there was an error connecting to the server.') };
            if (!isAbort) console.error('[CompanyChat] /agent/query error:', err);
            setMessagesForConv(convId, prev => [...prev, stoppedMsg]);
            persistTurn(convId, stoppedMsg);
            if (convId !== conversationIdRef.current) markUnread(convId);
        } finally {
            _abortByConv.delete(convId);
            stopRunning(convId);
        }
    };

    // Build the confirmed plan: merge user radio selections into the original plan.
    // Two sources of selections:
    //   1. clarifier_options[] groups (keyed by field) — backend-driven, preferred shape.
    //   2. legacy missing-field detection — for older plans that don't include clarifier_options[].
    const buildConfirmedPlan = (clar, idx) => {
        const plan = clar.plan;
        if (!plan) return plan;
        const selections = clarifierSelections[idx] || {};
        const next = { ...plan, confirmed_by_user: true, needs_confirmation: false };

        // (1) Apply backend-driven clarifier_options selections.
        const groups = groupClarifierOptions(clar.clarifierOptions);
        const coveredFields = new Set(Object.keys(groups));
        for (const [field, groupOpts] of Object.entries(groups)) {
            const optIdx = selections[field] ?? defaultIndexFor(groupOpts);
            if (optIdx == null) continue;
            const opt = groupOpts[optIdx];
            if (!opt) continue;
            // If the option's value is a structured object, merge it; otherwise set scalar.
            if (opt.value && typeof opt.value === 'object' && !Array.isArray(opt.value)) {
                next[field] = {
                    ...(typeof plan[field] === 'object' && plan[field] !== null ? plan[field] : {}),
                    ...opt.value,
                };
            } else {
                next[field] = opt.value;
            }
        }

        // (2) Legacy: apply selections from the missing-fields path for any field
        // not already covered by clarifier_options[].
        const missing = detectMissingFields(plan);
        for (const { key, value } of missing) {
            if (coveredFields.has(key)) continue;
            const optIdx = selections[key];
            if (optIdx == null) continue;
            const opts = optionsForField(key, value);
            const opt = opts[optIdx];
            if (!opt) continue;
            next[key] = {
                ...value,
                ...opt.patch,
                confidence: 1,
                source: 'user_confirmed',
                missing: false,
                hint: null,
            };
        }

        // Free-text topic answer (V1 fallback when Flask doesn't supply
        // clarifier_options[] with topic alternatives).
        const typedTopic = clarifierTopicText[idx]?.trim();
        if (typedTopic) {
            next.topic = {
                ...(plan.topic && typeof plan.topic === 'object' ? plan.topic : {}),
                topic_name: typedTopic,
                missing: false,
                confidence: 1,
                source: 'user_typed',
                hint: null,
            };
        }

        // Echo the suggested_mode the agent originally proposed.
        if (clar.suggestedMode) next.suggested_mode = clar.suggestedMode;

        return next;
    };

    // User accepted a clarifier — re-POST with confirmed_plan to execute
    const confirmClarifier = async (clarifierIdx) => {
        const clar = messages[clarifierIdx];
        if (!clar || clar.type !== 'clarifier') return;
        const confirmedPlan = buildConfirmedPlan(clar, clarifierIdx);
        const convId = clar.conversationId || conversationId;
        if (!convId) return;
        // Mark clarifier as resolved so the buttons hide
        setMessagesForConv(convId, prev => prev.map((m, i) => (i === clarifierIdx ? { ...m, _resolved: true } : m)));
        startRunning(convId);
        const abortCtrl = new AbortController();
        _abortByConv.set(convId, abortCtrl);
        try {
            const body = {
                question: clar.originalQuestion,
                confirmed_plan: confirmedPlan,
                conversation_id: convId,
            };
            if (companyId) body.company_id = companyId;
            if (orgId) body.organization_id = orgId;
            if (userId) body.user_id = userId;
            const data = await callAgent(body, { signal: abortCtrl.signal });
            const { msg: agentMsg } = appendAgentResponse(data, clar.originalQuestion, convId);
            persistTurn(convId, agentMsg);
            setConversations(prev => prev.map(c => (
                c.id === convId ? { ...c, updated_at: new Date().toISOString() } : c
            )));
            if (convId !== conversationIdRef.current) {
                markUnread(convId);
                maybeFireBrowserNotification(convId, agentMsg);
            }
        } catch (err) {
            const isAbort = err?.name === 'AbortError';
            const errMsg = { type: 'error', content: isAbort ? 'Stopped.' : (err?.message || 'Confirmation failed.') };
            if (!isAbort) console.error('[CompanyChat] confirm error:', err);
            setMessagesForConv(convId, prev => [...prev, errMsg]);
            persistTurn(convId, errMsg);
            if (convId !== conversationIdRef.current) markUnread(convId);
        } finally {
            _abortByConv.delete(convId);
            stopRunning(convId);
        }
    };

    const setSelection = (msgIdx, fieldKey, optIdx) => {
        setClarifierSelections(prev => ({
            ...prev,
            [msgIdx]: { ...(prev[msgIdx] || {}), [fieldKey]: optIdx },
        }));
    };

    // ─── Sidebar handlers ─────────────────────────────────────────────

    // Reset to a blank chat. Other conversations keep running in the background;
    // their state lives in the module store, not in this component's state.
    const handleNewChat = () => {
        setInput('');
        setConversationId(null);
        setClarifierSelections({});
        setClarifierTopicText({});
        setEditingIdx(null);
        setEditingDraft('');
    };

    // Click an item in the sidebar — switch view. Loads from store cache if
    // present (e.g., this conv just finished running); otherwise fetches.
    // Switching is allowed even while a chat is running — the running chat
    // keeps writing back to its own slot in the store.
    const handleSelectConversation = async (id) => {
        if (!id || id === conversationId) return;
        setConversationId(id);
        setClarifierSelections({});
        setClarifierTopicText({});
        setEditingIdx(null);
        setEditingDraft('');
        markRead(id);

        // If we don't have cached messages for this conv, fetch them.
        if (getMessagesForConv(id).length === 0) {
            try {
                const row = await getConversation(id);
                const turns = Array.isArray(row?.turns) ? row.turns : [];
                const restored = turns.map(turnToMessage).filter(Boolean);
                const cleaned = restored.map(m => (
                    m.type === 'clarifier' ? { ...m, _resolved: true } : m
                ));
                setMessagesForConv(id, cleaned);
                // Stamp current company onto the conversation if not already there.
                if (companyId && !(row?.company_ids || []).includes(companyId)) {
                    updateConversation(id, { append_company_id: companyId }).catch(() => {});
                    setConversations(prev => prev.map(c => (
                        c.id === id
                            ? { ...c, company_ids: [...(c.company_ids || []), companyId] }
                            : c
                    )));
                }
            } catch (err) {
                console.warn('[CompanyChat] getConversation failed:', err?.message);
            }
        } else if (companyId) {
            // Cached. Still stamp company append.
            const conv = conversations.find(c => c.id === id);
            if (conv && !(conv.company_ids || []).includes(companyId)) {
                updateConversation(id, { append_company_id: companyId }).catch(() => {});
                setConversations(prev => prev.map(c => (
                    c.id === id
                        ? { ...c, company_ids: [...(c.company_ids || []), companyId] }
                        : c
                )));
            }
        }
    };

    const handleDeleteConversation = async (id, e) => {
        e?.stopPropagation();
        if (!id) return;
        if (!window.confirm('Delete this chat? This cannot be undone.')) return;
        // Optimistic
        setConversations(prev => prev.filter(c => c.id !== id));
        if (id === conversationId) handleNewChat();
        try {
            await deleteConversation(id);
        } catch (err) {
            console.warn('[CompanyChat] deleteConversation failed:', err?.message);
            // Rollback list refresh
            refreshConversations();
        }
    };

    // ─── Edit-fork on the last user message ───────────────────────────

    const handleStartEdit = (idx) => {
        if (isLoading) return;
        if (idx !== lastUserMessageIdx) return;
        const msg = messages[idx];
        setEditingIdx(idx);
        setEditingDraft(msg?.content || '');
    };

    const handleCancelEdit = () => {
        setEditingIdx(null);
        setEditingDraft('');
    };

    const handleSaveEdit = async () => {
        const draft = editingDraft.trim();
        const idx = editingIdx;
        const convId = conversationId;
        if (!draft || idx == null || !convId) return;
        // Build the new local thread: keep everything before the edited user
        // message, replace it, drop everything after (the old assistant turn).
        const before = messages.slice(0, idx);
        const editedUser = { type: 'user', content: draft };
        const newMessages = [...before, editedUser];
        setMessagesForConv(convId, newMessages);
        setEditingIdx(null);
        setEditingDraft('');
        startRunning(convId);

        // Mirror the truncation to the backend, then send as a fresh turn.
        const newTurns = newMessages.map(m => ({
            role: m.type,
            content: messageToTurnContent(m),
            company_id: companyId || null,
        }));
        updateConversation(convId, { turns: newTurns }).catch(err => {
            console.warn('[CompanyChat] edit-fork PATCH failed:', err?.message);
        });

        const abortCtrl = new AbortController();
        _abortByConv.set(convId, abortCtrl);
        try {
            const body = {
                question: draft,
                conversation_id: convId,
            };
            if (companyId) body.company_id = companyId;
            if (orgId) body.organization_id = orgId;
            if (userId) body.user_id = userId;
            const data = await callAgent(body, { signal: abortCtrl.signal });
            const { msg: agentMsg } = appendAgentResponse(data, draft, convId);
            persistTurn(convId, agentMsg);
            setConversations(prev => prev.map(c => (
                c.id === convId ? { ...c, updated_at: new Date().toISOString() } : c
            )));
            if (convId !== conversationIdRef.current) {
                markUnread(convId);
                maybeFireBrowserNotification(convId, agentMsg);
            }
        } catch (err) {
            const isAbort = err?.name === 'AbortError';
            const errMsg = { type: 'error', content: isAbort ? 'Stopped.' : (err?.message || 'Failed to refire query.') };
            if (!isAbort) console.error('[CompanyChat] edit-fork agent error:', err);
            setMessagesForConv(convId, prev => [...prev, errMsg]);
            persistTurn(convId, errMsg);
            if (convId !== conversationIdRef.current) markUnread(convId);
        } finally {
            _abortByConv.delete(convId);
            stopRunning(convId);
        }
    };

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!input.trim() || isLoading) return;
        const userMessage = input.trim();
        setInput('');
        await sendMessage(userMessage);
    };

    const handleExampleClick = (question) => {
        if (!isLoading) sendMessage(question);
    };

    // ── Renderers ────────────────────────────────────────────────

    const renderTimeseries = (ts) => {
        const norm = normaliseTimeseries(ts);
        if (!norm) return null;
        return (
            <div className="chat-timeseries">
                <div className="chat-timeseries-label">
                    {norm.label} <span className="chat-timeseries-axis-hint">over time</span>
                </div>
                <ResponsiveContainer width="100%" height={160}>
                    <LineChart data={norm.data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis
                            dataKey={norm.xKey}
                            tick={{ fontSize: 10 }}
                            tickFormatter={formatTick}
                            minTickGap={32}
                        />
                        <YAxis tick={{ fontSize: 10 }} width={36} />
                        <RTooltip
                            labelFormatter={formatTick}
                            formatter={(v) => [v, norm.label]}
                        />
                        <Line
                            type="monotone"
                            dataKey={norm.yKey}
                            name={norm.label}
                            stroke="#1042C1"
                            strokeWidth={2}
                            dot={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    };

    const renderSources = (sources) => {
        if (!Array.isArray(sources) || sources.length === 0) return null;
        return (
            <div className="chat-sources">
                <div className="chat-sources-label">Sources</div>
                <ol className="chat-sources-list">
                    {sources.map((s, i) => {
                        const id = typeof s === 'string' ? s : (s.id || s.url || '');
                        const label = typeof s === 'string' ? s : (s.title || s.url || s.id || `Source ${i + 1}`);
                        const href = typeof s === 'string'
                            ? `/post/${s}`
                            : (s.url || (s.id ? `/post/${s.id}` : '#'));
                        return (
                            <li key={`${id}-${i}`}>
                                <a href={href} target="_blank" rel="noopener noreferrer">{label}</a>
                            </li>
                        );
                    })}
                </ol>
            </div>
        );
    };

    const renderClarifier = (m, idx) => {
        const groups = groupClarifierOptions(m.clarifierOptions);
        const groupFields = Object.keys(groups);
        const coveredFields = new Set(groupFields);
        const missing = detectMissingFields(m.plan).filter(({ key }) => !coveredFields.has(key));
        const selections = clarifierSelections[idx] || {};

        // For each backend-driven group, the effective selection is either the user's
        // pick or the option flagged `suggested: true`. Default-suggested counts as answered.
        const groupAnswered = groupFields.every(field => {
            const groupOpts = groups[field];
            const def = defaultIndexFor(groupOpts);
            return (selections[field] ?? def) != null;
        });
        // Legacy missing-field path requires an explicit click.
        const legacyAnswered = missing.every(({ key, value }) => {
            const opts = optionsForField(key, value);
            if (opts.length === 0) {
                // Topic with no preset alternatives needs a typed name.
                if (key === 'topic') return !!(clarifierTopicText[idx]?.trim());
                // Other fields with no presets pass through; agent re-prompts if needed.
                return true;
            }
            return selections[key] != null;
        });
        const allAnswered = groupAnswered && legacyAnswered;

        const fieldDisplayLabel = (field, groupOpts) => {
            // Prefer a backend-supplied group label; fall back to FIELD_LABELS dictionary; then the raw field key.
            const fromOpt = groupOpts.find(o => o.field_label || o.group_label);
            return (fromOpt && (fromOpt.field_label || fromOpt.group_label))
                || FIELD_LABELS[field]
                || field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        };

        return (
            <div className="message-content clarifier">
                <div className="clarifier-prompt">{renderBoldMarkdown(m.followUp)}</div>

                {/* Backend-driven groups (clarifier_options[]) */}
                {groupFields.map(field => {
                    const groupOpts = groups[field];
                    const def = defaultIndexFor(groupOpts);
                    const selectedIdx = selections[field] ?? def;
                    return (
                        <div key={field} className="clarifier-field">
                            <div className="clarifier-field-label">
                                {fieldDisplayLabel(field, groupOpts)}
                            </div>
                            <div className="clarifier-chip-row" role="radiogroup">
                                {groupOpts.map((opt, optIdx) => {
                                    const checked = selectedIdx === optIdx;
                                    const overrideKey = `${field}:${opt.value}`;
                                    const display = CHIP_LABEL_OVERRIDES[overrideKey]
                                        || opt.label
                                        || String(opt.value);
                                    return (
                                        <button
                                            key={`${opt.value}-${optIdx}`}
                                            type="button"
                                            role="radio"
                                            aria-checked={checked}
                                            className={`clarifier-chip${checked ? ' clarifier-chip--active' : ''}`}
                                            onClick={() => setSelection(idx, field, optIdx)}
                                            disabled={m._resolved || isLoading}
                                        >
                                            {display}
                                            {opt.suggested && <span className="clarifier-chip-badge"> · suggested</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}

                {/* Legacy missing-field path (only shown for fields not in clarifier_options[]) */}
                {missing.map(({ key, value }) => {
                    const opts = optionsForField(key, value);
                    // Topic with no chips → free-text input fallback.
                    if (opts.length === 0 && key === 'topic') {
                        return (
                            <div key={key} className="clarifier-field">
                                <div className="clarifier-field-label">{FIELD_LABELS.topic}</div>
                                {value.hint && (
                                    <div className="clarifier-field-hint">{value.hint}</div>
                                )}
                                <input
                                    type="text"
                                    className="clarifier-text-input"
                                    placeholder="Type the topic name (e.g. Carnival)"
                                    value={clarifierTopicText[idx] || ''}
                                    onChange={(e) => setClarifierTopicText(prev => ({ ...prev, [idx]: e.target.value }))}
                                    disabled={m._resolved || isLoading}
                                />
                            </div>
                        );
                    }
                    if (opts.length === 0) return null;
                    return (
                        <div key={key} className="clarifier-field">
                            <div className="clarifier-field-label">
                                {FIELD_LABELS[key] || key}
                            </div>
                            {value.hint && (
                                <div className="clarifier-field-hint">{value.hint}</div>
                            )}
                            <div className="clarifier-chip-row" role="radiogroup">
                                {opts.map((opt, optIdx) => {
                                    const checked = selections[key] === optIdx;
                                    return (
                                        <button
                                            key={opt.label}
                                            type="button"
                                            role="radio"
                                            aria-checked={checked}
                                            className={`clarifier-chip${checked ? ' clarifier-chip--active' : ''}`}
                                            onClick={() => setSelection(idx, key, optIdx)}
                                            disabled={m._resolved || isLoading}
                                        >
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}

                {m.plan && (
                    <details className="clarifier-plan">
                        <summary>View full plan</summary>
                        <pre>{JSON.stringify(m.plan, null, 2)}</pre>
                    </details>
                )}

                {!m._resolved && (
                    <div className="clarifier-actions">
                        <button
                            className="clarifier-confirm"
                            onClick={() => confirmClarifier(idx)}
                            disabled={isLoading || !allAnswered}
                            title={!allAnswered ? 'Pick an option for each prompt above' : ''}
                        >
                            Confirm and run
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderAssistant = (m) => {
        let footnoteCounter = 0;
        return (
            <div className="message-content">
                {m.headline && (
                    <div className="answer-headline">{renderBoldMarkdown(m.headline)}</div>
                )}
                <div className="answer-text">
                    {m.narrative.map((item, i) => (
                        <p key={i} className="answer-paragraph">
                            {renderBoldMarkdown(item.comment)}
                            {Array.isArray(item.sources) && item.sources.length > 0 && (
                                <span className="source-references">
                                    {item.sources.map((sourceId, sourceIdx) => {
                                        footnoteCounter++;
                                        const isLast = sourceIdx === item.sources.length - 1;
                                        return (
                                            <a
                                                key={`${sourceId}-${footnoteCounter}`}
                                                href={`/post/${sourceId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="source-reference"
                                                title="View source post"
                                            >
                                                <sup>{footnoteCounter}{!isLast && ','}&nbsp;</sup>
                                            </a>
                                        );
                                    })}
                                </span>
                            )}
                        </p>
                    ))}
                </div>
                {renderTimeseries(m.timeseries)}
                {renderSources(m.sources)}
            </div>
        );
    };

    const renderMessage = (message, idx) => {
        switch (message.type) {
            case 'user':
                return <div className="message-content">{message.content}</div>;
            case 'assistant':
                return renderAssistant(message);
            case 'clarifier':
                return renderClarifier(message, idx);
            case 'error':
                return <div className="message-content error">{message.content}</div>;
            default:
                return null;
        }
    };

    const exampleQuestions = [
        "What are the key customer pain points mentioned?",
        "How does their product quality compare to competitors?",
        "What are the main reasons customers switch away?"
    ];

    return (
        <div className="company-chat card card-md">
            {/* ── Sidebar: chat history ─────────────────────────────── */}
            <aside className="company-chat-sidebar">
                <div className="chat-sidebar-header">
                    <button
                        className="chat-sidebar-new"
                        onClick={handleNewChat}
                        disabled={isLoading}
                    >
                        <Plus size={14} /> New chat
                    </button>
                </div>
                <div className="chat-sidebar-list">
                    {conversationsLoading && conversations.length === 0 && (
                        <div className="chat-sidebar-empty">Loading…</div>
                    )}
                    {!conversationsLoading && conversations.length === 0 && (
                        <div className="chat-sidebar-empty">No chats yet</div>
                    )}
                    {conversations.map(conv => {
                        const isActive = conv.id === conversationId;
                        const running = isRunning(conv.id);
                        const unread = isUnread(conv.id);
                        const multiCompany = Array.isArray(conv.company_ids) && conv.company_ids.length > 1;
                        return (
                            <div
                                key={conv.id}
                                className={`chat-sidebar-row${isActive ? ' chat-sidebar-row--active' : ''}${unread && !isActive ? ' chat-sidebar-row--unread' : ''}`}
                                onClick={() => handleSelectConversation(conv.id)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') handleSelectConversation(conv.id);
                                }}
                            >
                                {running ? (
                                    <CircularProgress size={12} thickness={5} className="chat-sidebar-spinner" />
                                ) : (
                                    <MessageSquare size={14} className="chat-sidebar-icon" />
                                )}
                                <div className="chat-sidebar-row-body">
                                    <div className="chat-sidebar-title">
                                        {conversationDisplayTitle(conv)}
                                        {multiCompany && <span className="chat-sidebar-multi" title={`${conv.company_ids.length} companies`}>·{conv.company_ids.length}</span>}
                                        {unread && !isActive && <span className="chat-sidebar-unread-dot" title="New result" />}
                                    </div>
                                    <div className="chat-sidebar-time">{formatUpdatedAt(conv.updated_at)}</div>
                                </div>
                                <button
                                    className="chat-sidebar-delete"
                                    onClick={(e) => handleDeleteConversation(conv.id, e)}
                                    title="Delete chat"
                                >
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            </aside>

            {/* ── Main: active conversation ─────────────────────────── */}
            <div className="company-chat-main">
                <div className="chat-messages">
                    {messages.length === 0 && (
                        <div className="empty-chat">
                            <h3>Research Assistant</h3>
                            <p>Ask questions to analyze customer feedback and market insights:</p>
                            <ul>
                                {exampleQuestions.map((question, index) => (
                                    <li
                                        key={index}
                                        onClick={() => handleExampleClick(question)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                handleExampleClick(question);
                                            }
                                        }}
                                    >
                                        {question}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {messages.map((message, index) => {
                        const isEditing = editingIdx === index;
                        const isEditableUser = message.type === 'user'
                            && index === lastUserMessageIdx
                            && !isLoading;
                        return (
                            <div key={index} className={`message ${message.type}`}>
                                {isEditing ? (
                                    <div className="message-content edit-active">
                                        <textarea
                                            className="edit-textarea"
                                            value={editingDraft}
                                            onChange={(e) => setEditingDraft(e.target.value)}
                                            rows={3}
                                            autoFocus
                                        />
                                        <div className="edit-actions">
                                            <button
                                                className="edit-save"
                                                onClick={handleSaveEdit}
                                                disabled={!editingDraft.trim()}
                                            >
                                                <Check size={14} /> Save & re-run
                                            </button>
                                            <button className="edit-cancel" onClick={handleCancelEdit}>
                                                <X size={14} /> Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {renderMessage(message, index)}
                                        {isEditableUser && (
                                            <button
                                                className="message-edit-btn"
                                                onClick={() => handleStartEdit(index)}
                                                title="Edit and re-run"
                                            >
                                                <Pencil size={12} />
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })}
                    {isLoading && (
                        <div className="message assistant loading">
                            <div className="thinking-text">Thinking</div>
                        </div>
                    )}
                </div>
                <form onSubmit={handleSubmit} className="chat-input-form">
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={isLoading ? "Working..." : (conversationId ? "Ask a follow-up..." : "Ask a question...")}
                        disabled={isLoading || editingIdx != null}
                    />
                    {isLoading ? (
                        <button
                            type="button"
                            className="chat-stop-btn"
                            onClick={() => abortConv(conversationId)}
                            title="Stop"
                        >
                            <Square size={14} fill="currentColor" />
                        </button>
                    ) : (
                        <button type="submit" disabled={!input.trim() || editingIdx != null}>
                            <SendIcon />
                        </button>
                    )}
                </form>
            </div>
        </div>
    );
};

export default CompanyChat;
